
import { defineComponent, ref, computed, PropType, watch } from 'vue'
import { defineRule, Field, useField } from 'vee-validate'
import { prefectures } from '@/lib/prefectures'
import { NumberFormatter } from '@/lib/number-formatter'
import { ShippingAddress } from '@/types/order'
import { required, email } from '@vee-validate/rules'

import FormattableField from './FormattableField.vue'

defineRule('required', required)
defineRule('email', email)

export default defineComponent({
  components: {
    Field,
    FormattableField
  },
  props: {
    address: {
      type: Object as PropType<ShippingAddress>,
      required: true
    },
    isRegistered: {
      type: Boolean,
      required: true
    }
  },
  emits: ['input'],
  setup (props, context) {
    const { value: addressField, validate: validateAddressField, errors: addressErrors } = useField<string>(
      'address1',
      (v: string) => {
        if (!String(v).trim().length) return 'required'
        if (props.address.suggestion === undefined || props.address.suggestion.addresses === undefined) return true
        if (!props.address.suggestion.addresses.includes(v)) return 'not matched'
        return true
      },
      {
        initialValue: props.address.address1,
        validateOnValueUpdate: false
      }
    )

    watch(() => props.address.address1, (newValue) => {
      addressField.value = newValue
    })

    const { value: prefField, validate: validatePrefField, errors: prefErrors } = useField<string>(
      'shipping-address-prefecture-code',
      (v: string) => {
        if (props.address.suggestion === undefined) return true
        return props.address.suggestion.prefectureCode === v
      },
      {
        initialValue: props.address.prefectureCode,
        validateOnValueUpdate: false
      }
    )

    const handleChangePref = (event: Event) => {
      const target = event.target as HTMLSelectElement
      emitInput(target.value, 'prefectureCode')
      validatePrefField()
    }

    watch(() => props.address.suggestion?.prefectureCode, (newValue) => {
      prefField.value = newValue || '01'
    })

    const showRegisteredAddress = ref(props.isRegistered)
    const prefectureName = computed(() => {
      return prefectures.find(p => p.code === props.address.prefectureCode)?.name || ''
    })

    const emitInput = (value: string, key: string) => {
      context.emit('input', { ...props.address, [key]: value })
    }

    const emitCheckboxInput = ({ target }: { target: HTMLInputElement }, key: string) => {
      context.emit('input', { ...props.address, [key]: target.checked })
    }

    const formatZipCode = (value: string) => {
      return NumberFormatter.zipCode(value)
    }

    const validatePhoneNumber = (value: string) => {
      if (!value.trim().length) return false
      const digitsCount = value.replace(/[^\d]/g, '').length
      return digitsCount === 10 || digitsCount === 11
    }

    const formatPhoneNumber = (value: string) => {
      return NumberFormatter.phoneNumber(value)
    }

    return {
      addressField,
      addressErrors,
      validateAddressField,
      prefField,
      prefErrors,
      validatePrefField,
      handleChangePref,
      prefectures,
      showRegisteredAddress,
      prefectureName,
      emitInput,
      emitCheckboxInput,
      formatZipCode,
      validatePhoneNumber,
      formatPhoneNumber
    }
  }
})
